import {ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit} from '@angular/core';
import {PaginationInstance} from 'ngx-pagination';
import {BehaviorSubject, combineLatest, forkJoin, Observable} from 'rxjs';
import {map, tap} from 'rxjs/operators';

import {Dialog, DataService, SelectionManager, SearchProductsQuery} from '@vendure/admin-ui/core';
import {PRODUCTS} from '../product-list/graphql';
import {Product, ProductList, SortOrder} from '../../generated-admin-types';
// import {SortOrder} from '../../generated-admin-types';

export type SearchItem = SearchProductsQuery['search']['items'][number];

// 单次查询已选项的最大数量，需小于服务端 adminListQueryLimit
const INITIAL_SELECTION_CHUNK_SIZE = 500;

@Component({
  selector: 'app-product-multi-selector-dialog',
  templateUrl: './product-multi-selector-dialog.component.html',
  styleUrls: ['./product-multi-selector-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductMultiSelectorDialogComponent implements OnInit, Dialog<SearchItem[]> {
  mode: 'product' | 'variant' = 'product';
  initialSelectionIds: string[] = [];
  items$: Observable<SearchItem[]>;
  facetValues$: Observable<SearchProductsQuery['search']['facetValues']>;
  searchTerm$ = new BehaviorSubject<string>('');
  searchFacetValueIds$ = new BehaviorSubject<string[]>([]);
  paginationConfig: PaginationInstance = {
    currentPage: 1,
    itemsPerPage: 25,
    totalItems: 1,
  };
  selectionManager: SelectionManager<SearchItem>;

  resolveWith: (result?: SearchItem[]) => void;
  private paginationConfig$ = new BehaviorSubject<PaginationInstance>(this.paginationConfig);

  constructor(private dataService: DataService, private changeDetector: ChangeDetectorRef) {}

  ngOnInit(): void {
    const idFn =
      this.mode === 'product'
        ? (a: SearchItem, b: SearchItem) => a.productId === b.productId
        : (a: SearchItem, b: SearchItem) => a.productVariantId === b.productVariantId;
    this.selectionManager = new SelectionManager<SearchItem>({
      multiSelect: true,
      itemsAreEqual: idFn,
      additiveMode: true,
    });
    // const searchQueryResult = this.dataService.product.searchProducts('', this.paginationConfig.itemsPerPage, 0);
    const searchQueryResult = this.dataService.query<{newProducts: ProductList}>(PRODUCTS, {});
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const result$ = combineLatest(this.searchTerm$, this.searchFacetValueIds$, this.paginationConfig$).subscribe(
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      ([term, facetValueIds, pagination]) => {
        const take = +pagination.itemsPerPage;
        const skip = (pagination.currentPage - 1) * take;
        return searchQueryResult.ref.refetch({
          // input: {
          //   skip,
          //   take,
          //   term,
          //   facetValueIds,
          //   groupByProduct: this.mode === 'product',
          // },
          sku: '',
          collectionId: '',
          state: null,
          options: {
            skip,
            take,
            filter: {
              freeGift: {
                eq: false,
              },
              // hidden: {
              //   eq: false,
              // },
              name: {
                contains: term,
              },
            },
            sort: {
              createdAt: SortOrder.Desc,
            },
          },
        });
      },
    );

    this.items$ = searchQueryResult.stream$.pipe(
      tap(data => {
        const tempItems = this.handleProductListToSearchResult(data.newProducts);
        this.paginationConfig.totalItems = data.newProducts.totalItems;
        this.selectionManager.setCurrentItems(tempItems as unknown as SearchProductsQuery['search']['items']);
      }),
      map(data => {
        const temp = this.handleProductListToSearchResult(
          data.newProducts,
        ) as unknown as SearchProductsQuery['search']['items'];
        console.log(temp);
        // this.changeDetector.detectChanges();
        return temp;
      }),
    );

    // this.facetValues$ = searchQueryResult.stream$.pipe(map(data => data.search.facetValues));

    if (this.initialSelectionIds.length) {
      // 分批拉取已选项，避免列表查询的默认条数限制导致只能回显部分已选项
      const idChunks = this.chunkIds(this.initialSelectionIds, INITIAL_SELECTION_CHUNK_SIZE);
      if (this.mode === 'product') {
        forkJoin(
          idChunks.map(
            ids =>
              this.dataService.product.getProducts({
                take: ids.length,
                filter: {
                  id: {
                    in: ids,
                  },
                },
              }).single$,
          ),
        ).subscribe(results => {
          const selected: SearchItem[] = [];
          results.forEach(({products}) => {
            products.items.forEach(product => {
              selected.push({
                productId: product.id,
                productName: product.name,
              } as SearchItem);
            });
          });
          this.selectionManager.selectMultiple(selected);
          this.changeDetector.markForCheck();
        });
      } else {
        forkJoin(
          idChunks.map(
            ids =>
              this.dataService.product.getProductVariants({
                take: ids.length,
                filter: {
                  id: {
                    in: ids,
                  },
                },
              }).single$,
          ),
        ).subscribe(results => {
          const selected: SearchItem[] = [];
          results.forEach(({productVariants}) => {
            productVariants.items.forEach(variant => {
              selected.push({
                productVariantId: variant.id,
                productVariantName: variant.name,
              } as SearchItem);
            });
          });
          this.selectionManager.selectMultiple(selected);
          this.changeDetector.markForCheck();
        });
      }
    }
  }

  private chunkIds(ids: string[], size: number): string[][] {
    const chunks: string[][] = [];
    for (let i = 0; i < ids.length; i += size) {
      chunks.push(ids.slice(i, i + size));
    }
    return chunks;
  }

  handleProductListToSearchResult(list: ProductList) {
    return list.items.map((item: Product) => {
      return {
        enabled: item.enabled,
        productId: item.id,
        productName: item.name,
        productAsset: {
          id: item.featuredAsset?.id,
          preview: item.featuredAsset?.preview,
          focalPoint: item.featuredAsset?.focalPoint,
        },
        productVariantId: item.variants[0]?.id,
        productVariantName: item.variants[0]?.name,
        productVariantAsset: item.variants[0]?.featuredAsset
          ? {
              id: item.variants[0]?.featuredAsset?.id,
              preview: item.variants[0]?.featuredAsset?.preview,
              focalPoint: item.variants[0]?.featuredAsset?.focalPoint,
            }
          : null,
        sku: item.variants[0]?.sku,
        channelIds: [],
      };
    });
  }

  trackByFn(index: number, item: SearchItem) {
    return item.productId;
  }

  setSearchTerm(term: string) {
    this.paginationConfig.currentPage = 1;
    this.searchTerm$.next(term);
  }
  setFacetValueIds(ids: string[]) {
    this.paginationConfig.currentPage = 1;
    const sym = new Set(ids);
    const res = Array.from(sym);
    console.log(res);
    // this.facetValues$ = res as unknown as Observable<SearchProductsQuery['search']['facetValues']>;
    this.searchFacetValueIds$.next(res);
  }

  toggleSelection(item: SearchItem, event: MouseEvent) {
    this.selectionManager.toggleSelection(item, event);
  }

  clearSelection() {
    this.selectionManager.selectMultiple([]);
  }

  isSelected(item: SearchItem) {
    return this.selectionManager.isSelected(item);
  }

  entityInfoClick(event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();
  }

  pageChange(page: number) {
    this.paginationConfig.currentPage = page;
    this.paginationConfig$.next(this.paginationConfig);
  }

  itemsPerPageChange(itemsPerPage: number) {
    this.paginationConfig.itemsPerPage = itemsPerPage;
    this.paginationConfig$.next(this.paginationConfig);
  }

  select() {
    this.resolveWith(this.selectionManager.selection);
  }

  cancel() {
    this.resolveWith();
  }
}
